import { Avatar, AvatarFallback, AvatarImage, Badge } from '@e2n/ui';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@e2n/ui';
import { useMitarbeiter } from '@/domain/navigation/benachrichtigungen/hooks/useMitarbeiter';
import { Mitarbeiter } from '@/domain/navigation/benachrichtigungen/types/Benachrichtungen';
import { SchichttauschAntrag } from '@/domain/navigation/benachrichtigungen/types/SchichttauschAntrag';
import { mapMarkierung } from '@/domain/navigation/benachrichtigungen/utils/mapMarkierung';
import { useStandortContext } from '@/features/standortContext/StandortContext';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faAnglesUpDown } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';

type StyleProps = CSSProperties & {
  '--dot-color': CSSProperties['color'];
};

// eslint-disable-next-line max-lines-per-function
export function SelectTauschPartner({
  antrag,
  value,
  onSelect,
}: {
  antrag: SchichttauschAntrag;
  value: Mitarbeiter | null;
  onSelect: (mitarbeiter: Mitarbeiter | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const standort = useStandortContext();
  const { data: mitarbeiter, loading } = useMitarbeiter({
    standortId: standort.currentStandort.id,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className="flex flex-row justify-between gap-1 w-full text-neutrals-900 text-sm font-normal"
          variant="outline-secondary"
          role="combobox">
          <span className="truncate">
            {value
              ? `${value.vorname} ${value.nachname}`
              : t(
                  `domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.tauschPartnerSelect.placeholder`,
                )}
          </span>
          <FontAwesomeIcon icon={faAnglesUpDown} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="sm:w-80 p-0">
        <Command
          filter={(value, search) => {
            const m = mitarbeiter.find((m) => m.id === value);
            const fullName = `${m?.vorname.toLowerCase() || ''} ${
              m?.nachname.toLowerCase() || ''
            }`;
            if (fullName.includes(search.toLowerCase())) return 1;
            return 0;
          }}>
          <CommandInput placeholder="Suchen" className="h-9" />
          <CommandList>
            {!loading && (
              <CommandEmpty className="bg-neutrals-100 text-neutrals-700 text-sm font-normal text-ellipsis text-center p-3">
                {t(
                  'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.tauschPartnerSelect.empty',
                )}
              </CommandEmpty>
            )}
            <CommandGroup>
              {loading && (
                <div className="flex flex-row justify-center p-4">
                  <FontAwesomeIcon
                    icon={faSpinnerThird}
                    size="2x"
                    spin
                    className="text-primary-blue-600"
                    style={
                      {
                        '--fa-secondary-color': '#D1D1D6',
                      } as React.CSSProperties
                    }
                  />
                </div>
              )}
              {!loading &&
                mitarbeiter
                  .filter((m) => antrag.mitarbeiter.id !== m.id)
                  .map((m) => (
                    <CommandItem
                      className="data-[selected=true]:rounded-md hover:bg-neutrals-N200"
                      key={m.id}
                      value={m.id}
                      onSelect={(currentValue) => {
                        onSelect(
                          currentValue === value?.id
                            ? null
                            : mitarbeiter.find(
                                (mitarbeiter) =>
                                  mitarbeiter.id === currentValue,
                              ) || null,
                        );

                        setOpen(false);
                      }}>
                      <div className="flex flex-row gap-3 items-start">
                        <Avatar className="size-8">
                          <AvatarImage src={m.bildUrl} />
                          <AvatarFallback>{`${m.vorname
                            .substring(0, 1)
                            .toUpperCase()}${m.nachname
                            .substring(0, 1)
                            .toUpperCase()}`}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col gap-1 items-start">
                          <span className="text-neutrals-900 text-sm font-normal">{`${m.vorname} ${m.nachname}`}</span>
                          <Badge
                            variant="secondary"
                            className="flex flex-row gap-1 bg-neutrals-N300 hover:bg-neutrals-N300 rounded-full">
                            <FontAwesomeIcon
                              icon={faCircle}
                              className="max-w-2.5 max-h-2.5 text-(--dot-color)"
                              style={
                                {
                                  '--dot-color':
                                    mapMarkierung[m.arbeitsbereich.markierung]
                                      .base,
                                } as StyleProps
                              }
                            />
                            <span className="text-neutrals-900 text-xs font-semibold">
                              {m.arbeitsbereich.name}
                            </span>
                          </Badge>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
